import React, { useEffect, useState } from 'react';
import axios from 'axios';
import MFASetup from './MFASetup';

const MFAManagement = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [showRegenCodes, setShowRegenCodes] = useState(false);
  const [password, setPassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [regenCode, setRegenCode] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [error, setError] = useState('');
  const [regenCodes, setRegenCodes] = useState([]);

  const token = localStorage.getItem('token');
  const baseURL = process.env.REACT_APP_API_BASE_URL || '';

  const fetchStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(
        `${baseURL}/api/users/mfa/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(data);
    } catch (e) {
      setError('Failed to fetch MFA status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchStatus();
    }
  }, [token]);

  const regenerateBackupCodes = async () => {
    setRegenLoading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${baseURL}/api/users/mfa/backup-codes/regenerate`,
        { code: regenCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegenCodes(data.backup_codes || []);
      setShowRegenCodes(false);
      setRegenCode('');
      fetchStatus();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to regenerate backup codes');
    } finally {
      setRegenLoading(false);
    }
  };

  const disableMFA = async () => {
    setDisableLoading(true);
    setError('');
    try {
      await axios.post(
        `${baseURL}/api/users/mfa/disable`,
        { password, mfa_code: disableCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDisable(false);
      setPassword('');
      setDisableCode('');
      fetchStatus();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to disable MFA');
    } finally {
      setDisableLoading(false);
    }
  };

  const copyToClipboard = (text, buttonElement) => {
    navigator.clipboard.writeText(text).then(() => {
      const originalText = buttonElement.textContent;
      buttonElement.textContent = 'Copied!';
      buttonElement.classList.add('bg-green-100', 'text-green-700');
      setTimeout(() => {
        buttonElement.textContent = originalText;
        buttonElement.classList.remove('bg-green-100', 'text-green-700');
      }, 2000);
    }).catch(() => {
      alert('Failed to copy to clipboard');
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-6 bg-gray-200 rounded w-32"></div>
        <div className="h-4 bg-gray-200 rounded w-48"></div>
        <div className="h-8 bg-gray-200 rounded w-24"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {status?.mfa_enabled ? (
        <div className="space-y-4">
          {/* MFA Enabled Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-green-700">MFA Enabled</span>
              </div>
              <div className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                ✓ Protected
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-green-800">Your account is protected</h4>
                <p className="text-sm text-green-700 mt-1">
                  Two-factor authentication is active. You have {status.backup_codes_remaining} backup codes remaining.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowDisable(true)}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
            >
              Disable MFA
            </button>
            
            {status.backup_codes_remaining < 5 && (
              <button
                onClick={() => setShowRegenCodes(true)}
                className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium"
              >
                Regenerate Backup Codes
              </button>
            )}
          </div>

          {/* New Backup Codes Display */}
          {regenCodes.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-yellow-800 mb-3">🔄 New Backup Codes Generated</h4>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {regenCodes.map((code, i) => (
                  <code key={i} className="bg-white border px-2 py-1 rounded text-xs text-center font-mono">
                    {code}
                  </code>
                ))}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={(e) => copyToClipboard(regenCodes.join('\n'), e.target)}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                >
                  Copy All
                </button>
                <button
                  onClick={() => setRegenCodes([])}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200 transition-colors"
                >
                  Hide
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* MFA Disabled Status */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-sm font-medium text-red-700">MFA Disabled</span>
            </div>
            <div className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
              ⚠ Unprotected
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-800">Enhance your security</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Enable MFA to add an extra layer of protection to your account using Google Authenticator, Microsoft Authenticator, or any compatible TOTP app.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowSetup(true)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Enable MFA
          </button>
        </div>
      )}

      {/* MFA Setup Modal */}
      {showSetup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[95vh] overflow-y-auto relative">
            <button
              onClick={() => setShowSetup(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-6">
              <MFASetup
                token={token}
                onComplete={() => {
                  setShowSetup(false);
                  fetchStatus();
                }}
                onClose={() => setShowSetup(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Disable MFA Modal */}
      {showDisable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative">
            <div className="p-6">
              <h4 className="text-lg font-semibold mb-4">Disable MFA</h4>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700">
                  ⚠️ This will remove the extra security layer from your account.
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                  <input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">MFA Code</label>
                  <input
                    type="text"
                    placeholder="6-digit code or backup code"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.trim())}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-mono"
                  />
                </div>
                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                    {error}
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowDisable(false);
                    setPassword('');
                    setDisableCode('');
                    setError('');
                  }}
                  className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-700"
                >
                  Cancel
                </button>
                <button
                  disabled={!password || !disableCode || disableLoading}
                  onClick={disableMFA}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {disableLoading ? 'Disabling...' : 'Disable MFA'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Backup Codes Modal */}
      {showRegenCodes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative">
            <div className="p-6">
              <h4 className="text-lg font-semibold mb-4">Regenerate Backup Codes</h4>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-700">
                  ⚠️ This will invalidate all existing backup codes and generate new ones.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">MFA Code</label>
                <input
                  type="text"
                  placeholder="6-digit code from authenticator app"
                  value={regenCode}
                  onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-mono tracking-widest"
                  maxLength={6}
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 mt-3">
                  {error}
                </div>
              )}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowRegenCodes(false);
                    setRegenCode('');
                    setError('');
                  }}
                  className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-700"
                >
                  Cancel
                </button>
                <button
                  disabled={regenCode.length !== 6 || regenLoading}
                  onClick={regenerateBackupCodes}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                >
                  {regenLoading ? 'Generating...' : 'Generate New Codes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MFAManagement;